import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import { SettingsDrawer } from "@components-shared/mobile/settings-drawer";
import { Button } from "@components/buttons";
import EventViewStore from "@state/stores/offer/event/EventViewStore.mobile";
import TeamStandingsStatsStore from "@state/stores/offer/statistics/TeamStandingsStatsStore";
import { availableTeamStandingsMobileFilterOptions } from "@state/stores/offer/statistics/TeamStandingsStatsStore";
import { observer } from "mobx-react";

import { EventContentSkeleton } from "../../EventContentSkeleton";
import { TeamStandingsSection } from "./TeamStandingsSection";

export default observer(function TeamStandings({
	store,
	isSettingsDrawerOpen,
	onClose,
}: {
	store: EventViewStore;
	isSettingsDrawerOpen: boolean;
	onClose: (value: boolean) => void;
}) {
	if (store.teamStandingsStatsStore.loader.isLoading) {
		return <EventContentSkeleton />;
	}

	if (!store.teamStandingsStatsStore.teamStandingsStats?.data) {
		return null;
	}

	return (
		<>
			<TeamStandingsStatsDrawer
				store={store.teamStandingsStatsStore}
				isSettingsDrawerOpen={isSettingsDrawerOpen}
				onClose={onClose}
			/>
			<TeamStandingsSection
				store={store.teamStandingsStatsStore}
			/>
		</>
	);
});

function TeamStandingsStatsDrawer(props: {
	store: TeamStandingsStatsStore;
	isSettingsDrawerOpen: boolean;
	onClose: (value: boolean) => void;
}) {
	const { t } = useTranslation();

	const [teamStandingsFilter, setTeamStandingsFilter] = useState<string>(
		props.store.teamStandingsFilter
	);

	const [teamStandingsActiveTab, setTeamStandingsActiveTab] =
		useState<string>(props.store.activeTab);

    const [teamStandingsTournamentGroup, setTeamStandingsTournamentGroup] = 
        useState<string>(props.store.teamStandingsTournamentGroup)

	return (
		<SettingsDrawer
			onClose={() => {
				props.onClose(false);
				setTeamStandingsActiveTab(props.store.activeTab);
				setTeamStandingsFilter(props.store.teamStandingsFilter);
                setTeamStandingsTournamentGroup(props.store.teamStandingsTournamentGroup);
			}}
			isOpen={props.isSettingsDrawerOpen}
		>
			<div className="drawer__header">
				<div className="drawer__heading">
					{t("EVENT.STATISTICS.TEAM_STANDINGS.TITLE")}
				</div>
			</div>

			<div className="drawer__body">
				<div className="drawer__section">
					<div className="drawer__label label u-mar--bottom--reset">
						{t("EVENT.HEADER.SELECT_TEAM_STANDINGS_STATS_FILTER")}
					</div>

					{props.store.availableTeamStandingsTabs &&
						props.store.availableTeamStandingsTabs.map((group) => (
							<div
								key={group.key}
								className="drawer__option input--radio"
							>
								<input
									type="radio"
									id={`radio-${group.key}`}
									name="teamStandingsTab"
									value={group.key}
									checked={
										teamStandingsActiveTab === group.key
									}
									onChange={() =>
										setTeamStandingsActiveTab(group.key)
									}
									className="input--radio__box"
								/>
								<label
									htmlFor={`radio-${group.key}`}
									className="input--radio__label"
								>
									{t(group.title)}
								</label>
							</div>
						))}
				</div>

				<div className="drawer__section">
					<div className="drawer__label label u-mar--bottom--reset">
						{t("EVENT.HEADER.SELECT_TEAM_STANDINGS_STATS_TABLE")}
					</div>

					{availableTeamStandingsMobileFilterOptions.map((group) => (
						<div
							key={group.key}
							className="drawer__option input--radio"
						>
							<input
								type="radio"
								id={`radio-${group.key}`}
								name="teamStandingsFilter"
								value={group.key}
								checked={teamStandingsFilter === group.key}
								onChange={() =>
									setTeamStandingsFilter(group.key)
								}
								className="input--radio__box"
							/>
							<label
								htmlFor={`radio-${group.key}`}
								className="input--radio__label"
							>
								{t(group.title)}
							</label>
						</div>
					))}
				</div>

                {props.store.teamStandingsStats?.isGroupType && (
                    <div className="drawer__section">
                        <div className="drawer__label label u-mar--bottom--reset">
                            {t("EVENT.HEADER.SELECT_TEAM_STANDINGS_TOURNAMENT_GROUP_STATS_TABLE")}
                        </div>

                        {props.store.availableTeamStandingsTournamentGroups.map((tournamentGroup) => (
                            <div
                                key={tournamentGroup}
                                className="drawer__option input--radio"
                            >
                                <input
                                    type="radio"
                                    id={`radio-${tournamentGroup}`}
                                    name="teamStandingsTournamentGroupFilter"
                                    value={tournamentGroup}
                                    checked={teamStandingsTournamentGroup === tournamentGroup}
                                    onChange={() =>
                                        setTeamStandingsTournamentGroup(tournamentGroup)
                                    }
                                    className="input--radio__box"
                                />
                                <label
                                    htmlFor={`radio-${tournamentGroup}`}
                                    className="input--radio__label"
                                >
                                    {tournamentGroup === "all" ? t("EVENT.STATISTICS.TEAM_STANDINGS.BUTTONS.ALL") : t(tournamentGroup)}
                                </label>
                            </div>
                        ))}
                    </div>
                )}
			</div>

			<div className="drawer__footer">
				<Button
					btnText={t("ACC_STATEMENT.BET_DETAILS.CLOSE_BTN")}
					onClick={() => {
						setTeamStandingsActiveTab(props.store.activeTab);
						setTeamStandingsFilter(props.store.teamStandingsFilter);
                        setTeamStandingsTournamentGroup(props.store.teamStandingsTournamentGroup);
						props.onClose(false);
					}}
					className="btn--med btn--block btn--primary--inverted drawer__action--secondary"
				/>

				<Button
					btnText={t("mybets:PAGE.FILTER_FORM.SUBMIT_BUTTON")}
					disabled={
						!(
							teamStandingsActiveTab !== props.store.activeTab ||
							teamStandingsFilter !==
								props.store.teamStandingsFilter ||
                            teamStandingsTournamentGroup !== props.store.teamStandingsTournamentGroup
						)
					}
					onClick={() => {
						if (
							!(
								teamStandingsActiveTab !==
									props.store.activeTab ||
								teamStandingsFilter !==
									props.store.teamStandingsFilter ||
                                teamStandingsTournamentGroup !==
                                    props.store.teamStandingsTournamentGroup                                
							)
						) {
							return;
						}

						props.store.activeTab = teamStandingsActiveTab;
						props.store.teamStandingsFilter = teamStandingsFilter;
                        props.store.teamStandingsTournamentGroup = teamStandingsTournamentGroup;
						props.onClose(false);
					}}
					className="btn--med btn--block btn--accent drawer__action--primary"
				/>
			</div>
		</SettingsDrawer>
	);
}
